// =============================================================================
// KeptAtField — where an item lives: a fleet vehicle, the depot, or typed
// (mig 432, 8 Oct 2026)
// =============================================================================
// James, 8 Oct 2026, on the typed "Kept at" box: "Should be a vehicle drop
// down and/or free text — could be assigned to a person or a car."
//
// One picker: Not set · Depot · every active vehicle · Somewhere else (type
// it). A vehicle is saved as its id so the same ute is never spelt three
// ways; Depot and typed places are saved as text.
// =============================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui";

export interface KeptAt { vehicleId: string | null; location: string | null }
interface Veh { vehicle_id: string; vehicle_code: string | null; rego: string | null; make: string | null; model: string | null }

export function vehicleLabel(v: { vehicle_code?: string | null; rego?: string | null; make?: string | null; model?: string | null } | null | undefined): string {
  if (!v) return "";
  return [v.vehicle_code, [v.make, v.model].filter(Boolean).join(" "), v.rego].filter(Boolean).join(" · ");
}

export function useFleetVehicles() {
  return useQuery({
    queryKey: ["kept_at_vehicles"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Veh[]> => {
      const { data, error } = await (supabase as any).from("vehicles")
        .select("vehicle_id, vehicle_code, rego, make, model").eq("is_active", true).order("vehicle_code");
      if (error) { console.error("kept-at vehicles:", error.message); return []; }
      return (data ?? []) as Veh[];
    },
  });
}

export default function KeptAtField({ value, onChange }: { value: KeptAt; onChange: (v: KeptAt) => void }) {
  const { data: vehicles = [] } = useFleetVehicles();
  const isDepot = !value.vehicleId && (value.location ?? "").trim().toLowerCase() === "depot";
  const [typing, setTyping] = useState(!value.vehicleId && !!value.location && !isDepot);
  const sel = value.vehicleId ? `v:${value.vehicleId}` : typing ? "other" : isDepot ? "depot" : "";
  const cls = "w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text outline-none focus:border-accent";

  return (
    <div className="space-y-2">
      <select className={cls} value={sel} onChange={e => {
        const v = e.target.value;
        if (v.startsWith("v:")) { setTyping(false); onChange({ vehicleId: v.slice(2), location: null }); }
        else if (v === "depot") { setTyping(false); onChange({ vehicleId: null, location: "Depot" }); }
        else if (v === "other") { setTyping(true);  onChange({ vehicleId: null, location: "" }); }
        else                    { setTyping(false); onChange({ vehicleId: null, location: null }); }
      }}>
        <option value="">— Not set —</option>
        <option value="depot">Depot</option>
        {vehicles.length > 0 && (
          <optgroup label="In a vehicle">
            {vehicles.map(v => <option key={v.vehicle_id} value={`v:${v.vehicle_id}`}>{vehicleLabel(v)}</option>)}
          </optgroup>
        )}
        {/* a vehicle since made inactive still shows as chosen */}
        {value.vehicleId && !vehicles.some(v => v.vehicle_id === value.vehicleId) && (
          <option value={`v:${value.vehicleId}`}>Vehicle no longer active</option>
        )}
        <option value="other">Somewhere else — type it</option>
      </select>
      {typing && (
        <Input value={value.location ?? ""} onChange={(e: any) => onChange({ vehicleId: null, location: e.target.value })}
               placeholder="e.g. Site container, home office" autoFocus />
      )}
    </div>
  );
}
